import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard, CalendarDays, Users, BookOpen, UserCog, FileText,
  Receipt, ShieldCheck, ClipboardCheck, Star, GraduationCap, Briefcase, Mail,
  Globe2, Settings, LogOut, ChevronLeft, ChevronRight, Sparkles, Shield, Building2, PenTool, X, LifeBuoy,
} from 'lucide-react';
import PetronilleModal from '../PetronilleModal';
import SignatureSetupModal from '../SignatureSetupModal';
import FeatureGate from '../FeatureGate';
import api from '../../api/client';
import { useFeatures } from '../../hooks/useFeatures';
import { useRoleModules } from '../../hooks/useRoleModules';
import { useAuth } from '../../contexts/AuthContext';
import Logo from '../Logo';

const NAV_GROUPS = [
  {
    label: 'Pilotage',
    items: [
      { to: '/app', label: 'Tableau de bord', icon: LayoutDashboard, end: true, moduleKey: 'dashboard' },
      { to: '/app/sessions', label: 'Sessions', icon: CalendarDays, badgeKey: 'sessions_active', moduleKey: 'sessions' },
    ],
  },
  {
    label: 'Formation',
    items: [
      { to: '/app/programmes', label: 'Programmes', icon: BookOpen, moduleKey: 'programmes' },
      { to: '/app/stagiaires', label: 'Apprenants', icon: Users, badgeKey: 'learners', moduleKey: 'stagiaires' },
      { to: '/app/entreprises', label: 'Entreprises', icon: Building2, badgeKey: 'companies', moduleKey: 'entreprises' },
      { to: '/app/formateurs', label: 'Formateurs', icon: UserCog, moduleKey: 'formateurs' },
      { to: '/app/elearning', label: 'E-learning', icon: GraduationCap, featureKey: 'e_learning', moduleKey: 'elearning' },
    ],
  },
  {
    label: 'Commercial',
    items: [
      { to: '/app/crm', label: 'CRM / Prospects', icon: Briefcase, moduleKey: 'crm' },
      { to: '/app/email', label: 'Boîte email', icon: Mail, moduleKey: 'email' },
      { to: '/app/devis', label: 'Devis', icon: FileText, badgeKey: 'quotes_pending', moduleKey: 'devis' },
      { to: '/app/factures', label: 'Factures', icon: Receipt, badgeKey: 'invoices_unpaid', moduleKey: 'factures' },
    ],
  },
  {
    label: 'Qualité & Suivi',
    items: [
      { to: '/app/qualiopi', label: 'Qualiopi', icon: ShieldCheck, moduleKey: 'qualiopi' },
      { to: '/app/emargements', label: 'Émargements', icon: ClipboardCheck, moduleKey: 'emargements' },
      { to: '/app/evaluations', label: 'Évaluations', icon: Star, moduleKey: 'evaluations' },
    ],
  },
  {
    label: 'Plateforme',
    items: [
      { to: '/app/extranet', label: 'Extranet clients', icon: Globe2, featureKey: 'extranet', moduleKey: 'extranet' },
      { to: '/app/audit', label: 'Journal d\u2019audit', icon: Shield, featureKey: 'audit_logs', moduleKey: 'audit' },
      { to: '/app/parametres', label: 'Paramètres', icon: Settings, moduleKey: 'parametres' },
      { to: '/app/aide', label: 'Aide & Documentation', icon: LifeBuoy },
    ],
  },
];

function formatBadge(n) {
  if (n === null || n === undefined) return null;
  if (n === 0) return null;
  if (n >= 1000) return `${Math.floor(n / 100) / 10}k`;
  return String(n);
}

export default function Sidebar({ collapsed, onToggle }) {
  const [petronille, setPetronille] = useState(false);
  const [counts, setCounts] = useState({});
  const { has: hasFeature, loading: featuresLoading } = useFeatures();
  const { has: hasModule, loading: modulesLoading } = useRoleModules();
  const { user } = useAuth();
  // Vague 112 — "Mon espace formateur" shortcut for users who have BOTH an
  // admin role and a linked trainer profile (typical: OF manager who also
  // delivers trainings). The post-login router stays on /app; this button is
  // the explicit opt-in to visit the trainer extranet.
  const hasLinkedTrainerProfile = !!user?.trainer_profile?.trainer_id;
  // Vague 96.7 — onboarding nudge: detect users with no signature configured
  const [signatureSetupOpen, setSignatureSetupOpen] = useState(false);
  const [signatureSource, setSignatureSource] = useState(null);
  const [signatureDismissed, setSignatureDismissed] = useState(() => localStorage.getItem('ifaap_sig_banner_dismissed') === '1');

  const fetchSignature = async () => {
    try {
      const { data } = await api.get('/user/signature');
      setSignatureSource(data?.source || null);
    } catch {/* silent */}
  };

  // Vague 96.10 — if user has an email_config with NO signature configured,
  // we still want the banner to appear (because the cfg is the canonical
  // source for the SMTP signature and the user might think the SMTP cfg is
  // enough — but they need to upload an image there too).
  const [activeCfgId, setActiveCfgId] = useState(null);
  const fetchActiveCfg = async () => {
    try {
      const { data } = await api.get('/email-configs/active');
      // API returns { active: bool, config: {...} } where config has the id
      setActiveCfgId(data?.config?.id || data?.id || null);
    } catch { setActiveCfgId(null); }
  };

  useEffect(() => {
    let mounted = true;
    const fetchCounts = async () => {
      try {
        const { data } = await api.get('/sidebar/counts');
        if (mounted) setCounts(data);
      } catch {
        // silently ignore — badges just won't show
      }
    };
    fetchCounts();
    fetchSignature();
    fetchActiveCfg();
    // Refresh every 30s to stay in sync with CRUD operations
    const interval = setInterval(fetchCounts, 30000);
    return () => { mounted = false; clearInterval(interval); };
  }, []);

  const showSignatureBanner = !collapsed && !signatureDismissed && signatureSource === 'default';
  const dismissBanner = () => {
    localStorage.setItem('ifaap_sig_banner_dismissed', '1');
    setSignatureDismissed(true);
  };
  return (
    <aside
      className={`bg-white border-r border-[#EFF0F6] flex flex-col transition-[width] duration-200 ${
        collapsed ? 'w-[76px]' : 'w-[260px]'
      }`}
    >
      {/* Logo */}
      <div className="h-16 flex items-center px-5 border-b border-[#EFF0F6] shrink-0">
        <div className="flex items-center gap-2.5" data-testid="sidebar-brand">
          <Logo size={36} />
          {!collapsed && (
            <span className="font-bold text-[19px] text-[#1A1B2E]" style={{ fontFamily: "'Satoshi','Plus Jakarta Sans',sans-serif", letterSpacing: '-0.02em' }}>
              <span className="text-[var(--ofm-primary)]">OF</span>Manager
            </span>
          )}
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-4 px-3">
        {NAV_GROUPS.map((group, gi) => (
          <div key={gi} className="mb-5">
            {!collapsed && (
              <div className="px-3 mb-2 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-[#8E8FA5]">
                {group.label}
              </div>
            )}
            <div className="flex flex-col gap-0.5">
              {group.items.filter((item) => {
                // Vague 99.8 — hide nav items the current role is not allowed to see.
                if (item.moduleKey && !modulesLoading && !hasModule(item.moduleKey)) return false;
                // Hide nav items locked behind a disabled module (only after features have loaded).
                if (!item.featureKey || featuresLoading) return true;
                return hasFeature(item.featureKey);
              }).map((item) => {
                const Icon = item.icon;
                const badgeValue = item.badgeKey ? formatBadge(counts[item.badgeKey]) : (item.badge || null);
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      `nav-item flex items-center gap-3 px-3 py-2.5 rounded-[10px] text-[14px] font-medium ${
                        isActive
                          ? 'active bg-[var(--brand-primary-soft-bg)] text-[var(--brand-primary)]'
                          : 'text-[#4A4B63] hover:bg-[#F6F7FB] hover:text-[#1A1B2E]'
                      } ${collapsed ? 'justify-center' : ''}`
                    }
                    title={collapsed ? item.label : undefined}
                  >
                    <Icon className="w-[18px] h-[18px] shrink-0" strokeWidth={2} />
                    {!collapsed && (
                      <>
                        <span className="truncate flex-1">{item.label}</span>
                        {badgeValue && (
                          <span
                            data-testid={`sidebar-badge-${item.badgeKey || 'static'}`}
                            className="text-[10.5px] font-semibold px-1.5 py-0.5 rounded-md bg-[var(--brand-primary-soft-bg)] text-[var(--brand-primary)] tabular-nums"
                          >
                            {badgeValue}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="border-t border-[#EFF0F6] p-3 shrink-0">
        {showSignatureBanner && (
          <div data-testid="sig-onboarding-banner"
            className="mb-3 p-3 rounded-xl bg-gradient-to-br from-[#FEF3E7] to-[#FFE4D6] border border-[#FED7AA] relative">
            <button onClick={dismissBanner} data-testid="sig-onboarding-dismiss"
              title="Masquer"
              className="absolute top-1.5 right-1.5 w-5 h-5 rounded-md hover:bg-white/50 flex items-center justify-center text-[#9A3412]">
              <X className="w-3 h-3" />
            </button>
            <div className="flex items-center gap-2 mb-1.5">
              <PenTool className="w-4 h-4 text-[#C2410C]" />
              <span className="text-[12.5px] font-bold text-[#9A3412]">Signature email manquante</span>
            </div>
            <p className="text-[11px] text-[#7C2D12] leading-snug mb-2">
              Ajoutez votre signature pro pour qu'elle soit automatiquement attachée à tous vos emails depuis le CRM.
            </p>
            <button data-testid="sig-onboarding-cta"
              onClick={() => setSignatureSetupOpen(true)}
              className="w-full text-[12px] font-semibold py-1.5 rounded-lg bg-white text-[#C2410C] hover:bg-[#FFF7ED] border border-[#FED7AA] transition-colors">
              Configurer maintenant →
            </button>
          </div>
        )}
        {!collapsed && (
          <FeatureGate name="ai_petronille" mode="hidden">
            <div className="mb-3 p-3 rounded-xl ofm-gradient-soft" data-testid="sidebar-petronille-card">
              <div className="flex items-center gap-2 mb-1.5">
                <Sparkles className="w-4 h-4 text-[var(--ofm-primary)]" />
                <span className="text-[12.5px] font-semibold text-[var(--ofm-primary-dark)]">OFia · IA</span>
              </div>
              <p className="text-[11.5px] text-[#5B5982] leading-snug mb-2">
                Votre assistante IA pour automatiser vos formations.
              </p>
              <button onClick={() => setPetronille(true)} className="w-full text-[12px] font-semibold py-1.5 rounded-lg bg-white text-[var(--ofm-primary)] hover:bg-[var(--ofm-primary-soft-bg)] transition-colors border border-[var(--ofm-primary-soft-border)]">
                Découvrir
              </button>
            </div>
          </FeatureGate>
        )}
        {hasLinkedTrainerProfile && !collapsed && (
          <a
            href="/trainer"
            data-testid="sidebar-trainer-space-link"
            className="mb-3 flex items-center gap-2 px-3 py-2 rounded-[10px] border border-[var(--brand-primary-soft-border,#C7D2FE)] bg-[var(--brand-primary-soft-bg,#EEF2FF)] text-[var(--brand-primary,#4F46E5)] text-[12.5px] font-semibold hover:brightness-[0.97] transition"
            title="Accéder à votre extranet formateur"
          >
            <GraduationCap className="w-4 h-4 shrink-0" />
            <span className="truncate">Mon espace formateur</span>
          </a>
        )}
        {hasLinkedTrainerProfile && collapsed && (
          <a
            href="/trainer"
            data-testid="sidebar-trainer-space-link"
            className="mb-3 w-full flex items-center justify-center py-2 rounded-[10px] border border-[var(--brand-primary-soft-border,#C7D2FE)] bg-[var(--brand-primary-soft-bg,#EEF2FF)] text-[var(--brand-primary,#4F46E5)]"
            title="Mon espace formateur"
          >
            <GraduationCap className="w-4 h-4" />
          </a>
        )}
        <button
          onClick={onToggle}
          className={`w-full flex items-center ${collapsed ? 'justify-center' : 'justify-between'} px-3 py-2 rounded-[10px] text-[13px] text-[#6B6C85] hover:bg-[#F6F7FB] transition-colors`}
        >
          {!collapsed && <span className="flex items-center gap-2"><LogOut className="w-4 h-4" /> Réduire</span>}
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </div>
      <PetronilleModal open={petronille} onOpenChange={setPetronille} />
      <SignatureSetupModal
        open={signatureSetupOpen}
        onClose={() => setSignatureSetupOpen(false)}
        activeCfgId={activeCfgId}
        onSaved={() => { fetchSignature(); fetchActiveCfg(); }}
      />
    </aside>
  );
}
